import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';

import { trimEnd, isString, isEmpty, get } from 'lodash';

import { environment } from '../../environments/environment';
import { Observable } from '@shared/rx';
import { isAbsoluteUrl } from '@shared/utils';
import { BackendApiType } from '@app/shared/types';
import { BACKEND_API_TYPE_FIELD_PATH } from '@app/shared/constants';

@Injectable()
export class UrlInterceptor implements HttpInterceptor {
  protected envBackendUrlMapping: Record<BackendApiType, string> = {
    [BackendApiType.XVI]: environment.xviBackendURI,
    [BackendApiType.DM]: environment.dmBackendURI,
    [BackendApiType.PORTAL]: environment.xprtBackendURI,
    [BackendApiType.RPT]: environment.xrptBackendURI,
    [BackendApiType.AUDIT_TRAIL]: environment.auditTrailURI,
  };
  public constructor() {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const patchedReq = request.clone({
      url: this.handleRelativeUrl(request),
      // remove api type field
      params: request.params.delete(BACKEND_API_TYPE_FIELD_PATH),
    });

    return next.handle(patchedReq);
  }

  /**
   * get safety backend url
   * will find url in this.envBackendUrlMapping, if not exist, will throw error
   *
   * @param type backend url type
   * @returns safety backend url
   */
  protected getSafetyBackendUrl(type: BackendApiType): string {
    // default type is BackendApiType.XVI
    const backendUrl = get(
      this.envBackendUrlMapping,
      type ?? BackendApiType.XVI,
    );

    return this.makeSafeBackendUrl(backendUrl);
  }

  protected makeSafeBackendUrl(url: string): string {
    if (!isString(url)) {
      throw new TypeError(
        `Can't make backend URL. Expected a \`string\`, got \`${typeof url}\``,
      );
    }
    if (isEmpty(url)) {
      throw new Error(`Can't make backend URL. Expected not an empty string.`);
    }

    return trimEnd(url, '/');
  }

  /**
   * Notice: Only requests to XVI Backend works with relative path
   */
  protected handleRelativeUrl({ url, params }: HttpRequest<any>): string {
    if (isAbsoluteUrl(url, false)) return url;

    const backendApiType: BackendApiType = params.get(
      BACKEND_API_TYPE_FIELD_PATH,
    ) as BackendApiType;
    const baseBackendUrl = this.getSafetyBackendUrl(backendApiType);
    const urlWithSlashPrefix = url.replace(/^(?=[^/])/, '/');

    return baseBackendUrl + urlWithSlashPrefix;
  }
}
